import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/providers/theme-provider";
import { AuthProvider } from "@/providers/auth-provider";
import ProtectedRoute from "@/components/ProtectedRoute";
import { queryClient } from "@/lib/query-client";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import ResetPassword from "./pages/ResetPassword";
import Catalog from "./pages/Catalog";
import BookClub from "./pages/BookClub";
import Thread from "./pages/Thread";
import Club from "./pages/Club";
import Challenges from "./pages/Challenges";
import Resources from "./pages/Resources";
import About from "./pages/About";
import AdminDashboard from "./pages/Admin";
import LibrarianDashboard from "./pages/Librarian";
import StudentDashboard from "./pages/Student";
import LibrarianLayout from "./components/librarian/LibrarianLayout";
import LibrarianRouteGuard from "./components/librarian/LibrarianRouteGuard";
import RouteRedirect from "./components/librarian/RouteRedirect";
import "./App.css";

// Lazy load consolidated components for better performance
const Books = lazy(() => import("./pages/librarian/Books"));
const BookNew = lazy(() => import("./pages/librarian/BookNew"));
const BookEdit = lazy(() => import("./pages/librarian/BookEdit"));
const BulkOps = lazy(() => import("./pages/librarian/BulkOps"));

// Lazy load librarian pages
const TaskManagement = lazy(() => import("./pages/librarian/TaskManagement"));
const TaskNew = lazy(() => import("./pages/librarian/TaskNew"));
const TaskEdit = lazy(() => import("./pages/librarian/TaskEdit"));
const MemberManagement = lazy(() => import("./pages/librarian/MemberManagement"));
const MemberNew = lazy(() => import("./pages/librarian/MemberNew"));
const MemberEdit = lazy(() => import("./pages/librarian/MemberEdit"));
const Analytics = lazy(() => import("./pages/librarian/Analytics"));
const Notifications = lazy(() => import("./pages/librarian/Notifications"));
const Settings = lazy(() => import("./pages/librarian/Settings"));
const Loans = lazy(() => import("./pages/librarian/Loans"));
const LoanNew = lazy(() => import("./pages/librarian/LoanNew"));
const LoanDetail = lazy(() => import("./pages/librarian/LoanDetail"));
const Authors = lazy(() => import("./pages/librarian/Authors"));
const AuthorDetail = lazy(() => import("./pages/librarian/AuthorDetail"));
const AuthorNew = lazy(() => import("./pages/librarian/AuthorNew"));
const AuthorEdit = lazy(() => import("./pages/librarian/AuthorEdit"));
const BookDetail = lazy(() => import("./pages/librarian/BookDetail"));
const LocationDetail = lazy(() => import("./pages/librarian/LocationDetail"));
const LocationNew = lazy(() => import("./pages/librarian/LocationNew"));
const LocationEdit = lazy(() => import("./pages/librarian/LocationEdit"));
const PublisherDetail = lazy(() => import("./pages/librarian/PublisherDetail"));
const PublisherNew = lazy(() => import("./pages/librarian/PublisherNew"));
const PublisherEdit = lazy(() => import("./pages/librarian/PublisherEdit"));
const GenreDetail = lazy(() => import("./pages/librarian/GenreDetail"));
const GenreNew = lazy(() => import("./pages/librarian/GenreNew"));
const GenreEdit = lazy(() => import("./pages/librarian/GenreEdit"));
const Publishers = lazy(() => import("./pages/librarian/Publishers"));
const Penalties = lazy(() => import("./pages/librarian/Penalties"));
const Genres = lazy(() => import("./pages/librarian/Genres"));
const Conditions = lazy(() => import("./pages/librarian/Conditions"));
const ConditionNew = lazy(() => import("./pages/librarian/ConditionNew"));
const ConditionEdit = lazy(() => import("./pages/librarian/ConditionEdit"));
const Locations = lazy(() => import("./pages/librarian/Locations"));
const OverdueNotices = lazy(() => import("./pages/librarian/OverdueNotices"));
const LibrarianBookClubs = lazy(() => import("./pages/librarian/BookClubs"));
const LibrarianChallenges = lazy(() => import("./pages/librarian/Challenges"));
const ChallengeNew = lazy(() => import("./pages/librarian/ChallengeNew"));
const ChallengeEdit = lazy(() => import("./pages/librarian/ChallengeEdit"));

// Lazy load admin pages
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboardPage = lazy(() => import("./pages/admin/Dashboard"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminUserNew = lazy(() => import("./pages/admin/UserNew"));
const AdminUserEdit = lazy(() => import("./pages/admin/UserEdit"));
const AdminUserDetail = lazy(() => import("./pages/admin/UserDetail"));
const AdminLoansPage          = lazy(() => import("./pages/admin/ModulePages").then(m => ({ default: m.AdminLoansPage })));
const AdminInventoryPage      = lazy(() => import("./pages/admin/ModulePages").then(m => ({ default: m.AdminInventoryPage })));
const AdminOverdueNoticesPage = lazy(() => import("./pages/admin/ModulePages").then(m => ({ default: m.AdminOverdueNoticesPage })));
const AdminPoliciesPage       = lazy(() => import("./pages/admin/ModulePages").then(m => ({ default: m.AdminPoliciesPage })));
const AdminReviewsPage        = lazy(() => import("./pages/admin/ModulePages").then(m => ({ default: m.AdminReviewsPage })));
const AdminReportsPage        = lazy(() => import("./pages/admin/ModulePages").then(m => ({ default: m.AdminReportsPage })));
const AdminNotificationsPage  = lazy(() => import("./pages/admin/ModulePages").then(m => ({ default: m.AdminNotificationsPage })));
const AdminSiteContentPage    = lazy(() => import("./pages/admin/ModulePages").then(m => ({ default: m.AdminSiteContentPage })));
const AdminConditionsPage     = lazy(() => import("./pages/admin/ModulePages").then(m => ({ default: m.AdminConditionsPage })));
const AdminSecurityPage       = lazy(() => import("./pages/admin/ModulePages").then(m => ({ default: m.AdminSecurityPage })));
const AdminSettingsPage       = lazy(() => import("./pages/admin/ModulePages").then(m => ({ default: m.AdminSettingsPage })));
const AdminSystemPage         = lazy(() => import("./pages/admin/ModulePages").then(m => ({ default: m.AdminSystemPage })));

const SuspenseFallback = () => (
  <div className="flex min-h-[240px] items-center justify-center p-6 text-sm text-muted-foreground">
    Loading librarian tools…
  </div>
);

// Route table without a router: App mounts it in BrowserRouter below; tests
// mount it in a memory router instead. Routers must never nest.
export const AppRoutes = () => (
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/catalog" element={<Catalog />} />
              <Route path="/book-clubs" element={<BookClub />} />
              <Route path="/book-clubs/threads/:id" element={<Thread />} />
              <Route path="/book-clubs/:slug" element={<Club />} />
              <Route path="/challenges" element={<Challenges />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/about" element={<About />} />
              {/* New AdminLayout: overview and users of its own; the catalogue pages are shared with /librarian */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <Suspense fallback={<SuspenseFallback />}>
                      <AdminLayout />
                    </Suspense>
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<Suspense fallback={<SuspenseFallback />}><AdminDashboardPage /></Suspense>} />
                <Route path="users" element={<Suspense fallback={<SuspenseFallback />}><AdminUsers /></Suspense>} />
                <Route path="users/new" element={<Suspense fallback={<SuspenseFallback />}><AdminUserNew /></Suspense>} />
                <Route path="users/:userId" element={<Suspense fallback={<SuspenseFallback />}><AdminUserDetail /></Suspense>} />
                <Route path="users/:userId/edit" element={<Suspense fallback={<SuspenseFallback />}><AdminUserEdit /></Suspense>} />
                {/* Catalogue: the same pages as /librarian/*, rooted at /admin by
                    AdminLayout's StaffAreaProvider */}
                <Route path="books"                  element={<Suspense fallback={<SuspenseFallback />}><Books /></Suspense>} />
                <Route path="books/new"              element={<Suspense fallback={<SuspenseFallback />}><BookNew /></Suspense>} />
                <Route path="books/:bookId"          element={<Suspense fallback={<SuspenseFallback />}><BookDetail /></Suspense>} />
                <Route path="books/:id/edit"         element={<Suspense fallback={<SuspenseFallback />}><BookEdit /></Suspense>} />
                <Route path="bulk-ops"               element={<Suspense fallback={<SuspenseFallback />}><BulkOps /></Suspense>} />
                <Route path="authors"                element={<Suspense fallback={<SuspenseFallback />}><Authors /></Suspense>} />
                <Route path="authors/new"            element={<Suspense fallback={<SuspenseFallback />}><AuthorNew /></Suspense>} />
                <Route path="authors/:authorId"      element={<Suspense fallback={<SuspenseFallback />}><AuthorDetail /></Suspense>} />
                <Route path="authors/:authorId/edit" element={<Suspense fallback={<SuspenseFallback />}><AuthorEdit /></Suspense>} />
                <Route path="publishers"             element={<Suspense fallback={<SuspenseFallback />}><Publishers /></Suspense>} />
                <Route path="publishers/new"         element={<Suspense fallback={<SuspenseFallback />}><PublisherNew /></Suspense>} />
                <Route path="publishers/:publisherId" element={<Suspense fallback={<SuspenseFallback />}><PublisherDetail /></Suspense>} />
                <Route path="publishers/:publisherId/edit" element={<Suspense fallback={<SuspenseFallback />}><PublisherEdit /></Suspense>} />
                <Route path="genres"                 element={<Suspense fallback={<SuspenseFallback />}><Genres /></Suspense>} />
                <Route path="genres/new"             element={<Suspense fallback={<SuspenseFallback />}><GenreNew /></Suspense>} />
                <Route path="genres/:genreId"        element={<Suspense fallback={<SuspenseFallback />}><GenreDetail /></Suspense>} />
                <Route path="genres/:genreId/edit"   element={<Suspense fallback={<SuspenseFallback />}><GenreEdit /></Suspense>} />
                {/* Operations */}
                <Route path="loans"           element={<Suspense fallback={<SuspenseFallback />}><AdminLoansPage /></Suspense>} />
                <Route path="inventory"       element={<Suspense fallback={<SuspenseFallback />}><AdminInventoryPage /></Suspense>} />
                <Route path="overdue-notices" element={<Suspense fallback={<SuspenseFallback />}><AdminOverdueNoticesPage /></Suspense>} />
                <Route path="policies"        element={<Suspense fallback={<SuspenseFallback />}><AdminPoliciesPage /></Suspense>} />
                <Route path="reviews"         element={<Suspense fallback={<SuspenseFallback />}><AdminReviewsPage /></Suspense>} />
                {/* System */}
                <Route path="reports"         element={<Suspense fallback={<SuspenseFallback />}><AdminReportsPage /></Suspense>} />
                <Route path="notifications"   element={<Suspense fallback={<SuspenseFallback />}><AdminNotificationsPage /></Suspense>} />
                <Route path="content"         element={<Suspense fallback={<SuspenseFallback />}><AdminSiteContentPage /></Suspense>} />
                <Route path="conditions"      element={<Suspense fallback={<SuspenseFallback />}><AdminConditionsPage /></Suspense>} />
                <Route path="security"        element={<Suspense fallback={<SuspenseFallback />}><AdminSecurityPage /></Suspense>} />
                <Route path="settings"        element={<Suspense fallback={<SuspenseFallback />}><AdminSettingsPage /></Suspense>} />
                <Route path="system"          element={<Suspense fallback={<SuspenseFallback />}><AdminSystemPage /></Suspense>} />
              </Route>
              {/* Legacy AdminDashboard: has its own sidebar/topbar; handles all other /admin/* routes */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <Suspense fallback={<SuspenseFallback />}>
                      <AdminDashboard />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/librarian" 
                element={
                  <LibrarianRouteGuard>
                    <LibrarianLayout />
                  </LibrarianRouteGuard>
                }
              >
                {/* Main dashboard route */}
                <Route index element={<LibrarianDashboard />} />
                
                {/* Consolidated book management route with tab support */}
                <Route
                  path="books"
                  element={
                    <Suspense fallback={<SuspenseFallback />}>
                      <Books />
                    </Suspense>
                  }
                />

                {/* New book page (full-page form, replaces in-modal wizard) */}
                <Route
                  path="books/new"
                  element={
                    <Suspense fallback={<SuspenseFallback />}>
                      <BookNew />
                    </Suspense>
                  }
                />

                {/* Edit book page */}
                <Route
                  path="books/:id/edit"
                  element={
                    <Suspense fallback={<SuspenseFallback />}>
                      <BookEdit />
                    </Suspense>
                  }
                />

                {/* Book record page */}
                <Route path="books/:bookId" element={<Suspense fallback={<SuspenseFallback />}><BookDetail /></Suspense>} />

                {/* Bulk operations route */}
                <Route
                  path="bulk-ops"
                  element={
                    <Suspense fallback={<SuspenseFallback />}>
                      <BulkOps />
                    </Suspense>
                  }
                />
                
                {/* Old book-management URL */}
                <Route 
                  path="book-management" 
                  element={
                    <RouteRedirect 
                      from="/librarian/book-management"
                      to="/librarian/books"
                      tabParam="catalog"
                      deprecationMessage="Book Management moved to the Books page."
                    />
                  } 
                />
                
                {/* Old issue, return and inventory URLs land on the matching Books tab */}
                <Route 
                  path="issue" 
                  element={
                    <RouteRedirect 
                      from="/librarian/issue"
                      to="/librarian/books"
                      tabParam="issue-return"
                      deprecationMessage="Issuing now happens on the Books page."
                    />
                  } 
                />
                
                <Route 
                  path="return" 
                  element={
                    <RouteRedirect 
                      from="/librarian/return"
                      to="/librarian/books"
                      tabParam="issue-return"
                      deprecationMessage="Returns now happen on the Books page."
                    />
                  } 
                />
                
                <Route 
                  path="inventory" 
                  element={
                    <RouteRedirect 
                      from="/librarian/inventory"
                      to="/librarian/books"
                      tabParam="inventory"
                      deprecationMessage="Inventory now lives on the Books page."
                    />
                  } 
                />
                
                {/* Tasks Management */}
                <Route 
                  path="tasks" 
                  element={
                    <Suspense fallback={<SuspenseFallback />}>
                      <TaskManagement />
                    </Suspense>
                  } 
                />
                
                {/* Member Management */}
                <Route 
                  path="members" 
                  element={
                    <Suspense fallback={<SuspenseFallback />}>
                      <MemberManagement />
                    </Suspense>
                  } 
                />
                
                {/* Analytics Dashboard */}
                <Route 
                  path="analytics" 
                  element={
                    <Suspense fallback={<SuspenseFallback />}>
                      <Analytics />
                    </Suspense>
                  } 
                />
                
                {/* Notifications */}
                <Route 
                  path="notifications" 
                  element={
                    <Suspense fallback={<SuspenseFallback />}>
                      <Notifications />
                    </Suspense>
                  } 
                />
                
                {/* Settings */}
                <Route
                  path="settings"
                  element={
                    <Suspense fallback={<SuspenseFallback />}>
                      <Settings />
                    </Suspense>
                  }
                />

                {/* Redesign additions */}
                <Route path="loans"      element={<Suspense fallback={<SuspenseFallback />}><Loans /></Suspense>} />
                <Route path="loans/new"  element={<Suspense fallback={<SuspenseFallback />}><LoanNew /></Suspense>} />
                <Route path="loans/:loanId" element={<Suspense fallback={<SuspenseFallback />}><LoanDetail /></Suspense>} />
                <Route path="authors"    element={<Suspense fallback={<SuspenseFallback />}><Authors /></Suspense>} />
                <Route path="authors/new" element={<Suspense fallback={<SuspenseFallback />}><AuthorNew /></Suspense>} />
                <Route path="authors/:authorId" element={<Suspense fallback={<SuspenseFallback />}><AuthorDetail /></Suspense>} />
                <Route path="authors/:authorId/edit" element={<Suspense fallback={<SuspenseFallback />}><AuthorEdit /></Suspense>} />
                <Route path="publishers" element={<Suspense fallback={<SuspenseFallback />}><Publishers /></Suspense>} />
                <Route path="publishers/new" element={<Suspense fallback={<SuspenseFallback />}><PublisherNew /></Suspense>} />
                <Route path="publishers/:publisherId" element={<Suspense fallback={<SuspenseFallback />}><PublisherDetail /></Suspense>} />
                <Route path="publishers/:publisherId/edit" element={<Suspense fallback={<SuspenseFallback />}><PublisherEdit /></Suspense>} />
                <Route path="penalties"  element={<Suspense fallback={<SuspenseFallback />}><Penalties /></Suspense>} />
                <Route path="genres"     element={<Suspense fallback={<SuspenseFallback />}><Genres /></Suspense>} />
                <Route path="genres/new" element={<Suspense fallback={<SuspenseFallback />}><GenreNew /></Suspense>} />
                <Route path="genres/:genreId" element={<Suspense fallback={<SuspenseFallback />}><GenreDetail /></Suspense>} />
                <Route path="genres/:genreId/edit" element={<Suspense fallback={<SuspenseFallback />}><GenreEdit /></Suspense>} />
                <Route path="conditions" element={<Suspense fallback={<SuspenseFallback />}><Conditions /></Suspense>} />
                <Route path="conditions/new" element={<Suspense fallback={<SuspenseFallback />}><ConditionNew /></Suspense>} />
                <Route path="conditions/:conditionId/edit" element={<Suspense fallback={<SuspenseFallback />}><ConditionEdit /></Suspense>} />
                <Route path="locations" element={<Suspense fallback={<SuspenseFallback />}><Locations /></Suspense>} />
                <Route path="locations/new" element={<Suspense fallback={<SuspenseFallback />}><LocationNew /></Suspense>} />
                <Route path="locations/:locationId" element={<Suspense fallback={<SuspenseFallback />}><LocationDetail /></Suspense>} />
                <Route path="locations/:locationId/edit" element={<Suspense fallback={<SuspenseFallback />}><LocationEdit /></Suspense>} />
                <Route path="overdue-notices" element={<Suspense fallback={<SuspenseFallback />}><OverdueNotices /></Suspense>} />
                <Route path="book-clubs" element={<Suspense fallback={<SuspenseFallback />}><LibrarianBookClubs /></Suspense>} />
                <Route path="challenges" element={<Suspense fallback={<SuspenseFallback />}><LibrarianChallenges /></Suspense>} />
                <Route path="challenges/new" element={<Suspense fallback={<SuspenseFallback />}><ChallengeNew /></Suspense>} />
                <Route path="challenges/:slug/edit" element={<Suspense fallback={<SuspenseFallback />}><ChallengeEdit /></Suspense>} />
                {/* Create/edit pages for the desk records (the lists above keep their drawers for detail) */}
                <Route path="tasks/new" element={<Suspense fallback={<SuspenseFallback />}><TaskNew /></Suspense>} />
                <Route path="tasks/:taskId/edit" element={<Suspense fallback={<SuspenseFallback />}><TaskEdit /></Suspense>} />
                <Route path="members/new" element={<Suspense fallback={<SuspenseFallback />}><MemberNew /></Suspense>} />
                <Route path="members/:memberId/edit" element={<Suspense fallback={<SuspenseFallback />}><MemberEdit /></Suspense>} />
              </Route>
              <Route 
                path="/student" 
                element={
                  <ProtectedRoute requireRoles={["student"]}>
                    <StudentDashboard />
                  </ProtectedRoute>
                }
              />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
);

const App = () => (
  <ThemeProvider defaultTheme="system">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;